// /home/heliowallet/frontend/hooks/useWallet.ts
'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  useAccount, 
  useBalance, 
  useChainId, 
  useSwitchChain,
  useWalletClient,
  usePublicClient,
  useSignMessage,
  useSignTypedData,
  useSendTransaction,
  useWaitForTransactionReceipt,
  useReadContract,
  useWriteContract
} from 'wagmi';
import { parseEther, parseUnits, formatUnits, isAddress, type Address } from 'viem';
import { useChainStore } from '@/stores/chainStore';
import { useSelectedRpc } from './useSelectedRpc';
import { useRpcPatcher } from './useRpcPatcher';
import { tokenManager } from '@/services/tokenManager';
import { priceService } from '@/services/priceService';
import { rpcManager } from '@/services/rpcManager';
import toast from 'react-hot-toast';
import type { Token } from '@/services/tokenManager';

// ERC20 ABI for transfer and balanceOf
const ERC20_ABI = [
  {
    name: 'transfer',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'to', type: 'address' },
      { name: 'amount', type: 'uint256' }
    ],
    outputs: [{ name: '', type: 'bool' }]
  },
  {
    name: 'balanceOf',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }]
  }
] as const;

export function useWallet() {
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();
  const walletClient = useWalletClient();
  const publicClient = usePublicClient();
  
  // Use selected RPC
  const { 
    selectedRpc,
    setSelectedRpc,
    publicClient: customPublicClient
  } = useSelectedRpc(chainId);
  
  // Patch wagmi transport when RPC changes
  useRpcPatcher(chainId, selectedRpc || undefined);
  
  // Use balance
  const { data: balance, refetch: refetchBalance } = useBalance({ address });

  // Store hooks
  const {
    chains,
    getSelectedRpc,
    getRpcHealth,
    updateRpcHealth,
    setLastSelectedChain,
    
    // Token management - simplified for new design
    getCustomTokensForWallet,
    addCustomTokenToWallet,
    removeCustomTokenFromWallet,
    clearScannedTokensForWallet,
    
    // Loading states
    setTokensLoading,
    setBalancesLoading,
    loadingStates,
    
    // Transactions
    addTransaction,
    updateTransactionStatus,
    removeTransaction,
    clearTransactions,
    
    // RPC Preferences
    getChainPreferences,
    setChainPreferences,
    setAutoSwitchRpc,
    incrementRpcSwitchCount
  } = useChainStore();
  
  // State
  const [isLoadingTokens, setIsLoadingTokens] = useState(false);
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);
  const [tokenPrices, setTokenPrices] = useState<Record<string, number>>({});
  const [nativeTokenPrice, setNativeTokenPrice] = useState<number | null>(null);
  const [rpcs, setRpcs] = useState<string[]>([]);
  const [tokenListVersion, setTokenListVersion] = useState(0); // Force re-render trigger
  const [isTestingRpc, setIsTestingRpc] = useState(false);
  const [importedWallet, setImportedWallet] = useState<any>(null);
  const [allTokensWithBalance, setAllTokensWithBalance] = useState<Token[]>([]);
  const [pendingTxInfo, setPendingTxInfo] = useState<{
    to: string;
    value: string;
    tokenAddress?: string;
    tokenSymbol?: string;
  } | null>(null);
  
  // Transaction hooks
  const { 
    sendTransaction, 
    data: sendTxHash,
    isPending: isSendingNative,
    error: sendError 
  } = useSendTransaction();
  
  const { 
    writeContract, 
    data: writeTxHash,
    isPending: isSendingToken,
    error: writeError 
  } = useWriteContract();
  
  // Wait for transaction confirmations
  const { isLoading: isConfirmingNative, isSuccess: isConfirmedNative } = useWaitForTransactionReceipt({
    hash: sendTxHash as Address,
  });
  
  const { isLoading: isConfirmingToken, isSuccess: isConfirmedToken } = useWaitForTransactionReceipt({
    hash: writeTxHash as Address,
  });

  // Track native transaction when hash is available
  useEffect(() => {
    if (sendTxHash && address && chainId && pendingTxInfo) {
      const transaction = {
        hash: sendTxHash,
        chainId,
        from: address,
        to: pendingTxInfo.to,
        value: pendingTxInfo.value,
        type: 'send' as const,
        status: 'pending' as const,
        timestamp: Date.now(),
        gasPrice: '0', // Will be updated when confirmed
        gasUsed: '0', // Will be updated when confirmed
        blockNumber: 0 // Will be updated when confirmed
      };
      
      addTransaction(address, chainId, transaction);
      setPendingTxInfo(null); // Clear pending info
    }
  }, [sendTxHash, address, chainId, pendingTxInfo, addTransaction]);

  // Track token transaction when hash is available
  useEffect(() => {
    if (writeTxHash && address && chainId && pendingTxInfo?.tokenAddress) {
      const transaction = {
        hash: writeTxHash,
        chainId,
        from: address,
        to: pendingTxInfo.to,
        value: pendingTxInfo.value,
        type: 'token' as const,
        tokenAddress: pendingTxInfo.tokenAddress,
        tokenSymbol: pendingTxInfo.tokenSymbol || 'TOKEN',
        status: 'pending' as const,
        timestamp: Date.now(),
        gasPrice: '0', // Will be updated when confirmed
        gasUsed: '0', // Will be updated when confirmed
        blockNumber: 0 // Will be updated when confirmed
      };
      
      addTransaction(address, chainId, transaction);
      setPendingTxInfo(null); // Clear pending info
    }
  }, [writeTxHash, address, chainId, pendingTxInfo, addTransaction]);
  
  // Get chain info
  const chainInfo = useMemo(() => {
    return chains[chainId?.toString()] || null;
  }, [chains, chainId]);

  // Custom refetch functions using selected RPC
  const customRefetchBalance = useCallback(async () => {
    if (!address) {
      return;
    }
    
    // Use customPublicClient if available, otherwise fallback to default publicClient
    const clientToUse = customPublicClient || publicClient;
    if (!clientToUse) {
      return;
    }
    
    try {
      const balanceResult = await clientToUse.getBalance({ 
        address: address as Address 
      });
      
      const balanceData = {
        decimals: 18,
        formatted: formatUnits(balanceResult, 18),
        symbol: chainInfo?.nativeCurrency?.symbol || 'ETH',
        value: balanceResult
      };
      
    } catch (error) {
      console.error('Failed to custom refetch balance:', error);
    }
  }, [address, customPublicClient, publicClient, selectedRpc, chainInfo]);

  const customRefetchGas = useCallback(async () => {
    if (!chainId) {
      return;
    }
    
    // Use customPublicClient if available, otherwise fallback to default publicClient
    const clientToUse = customPublicClient || publicClient;
    if (!clientToUse) {
      return;
    }
    
    try {
      const [gasPrice, feeHistory] = await Promise.allSettled([
        clientToUse.getGasPrice(),
        clientToUse.getFeeHistory({ 
          blockCount: 4, 
          rewardPercentiles: [25, 50, 75] 
        }).catch(() => null)
      ]);
      
    } catch (error) {
      console.error('Failed to custom refetch gas:', error);
    }
  }, [chainId, customPublicClient, publicClient, selectedRpc]);
  
  // Get tokens - custom tokens and tokens with balance
  const tokens = useMemo(() => {
    if (!chainId || !address) {
      return [];
    }
    
    const customTokens = getCustomTokensForWallet(address, chainId);
    const safeCustomTokens = Array.isArray(customTokens) ? customTokens : [];
    
    // Merge with tokens that have balance > 0
    const tokensMap = new Map<string, Token>();
    
    // Add custom tokens first (always show them regardless of balance)
    safeCustomTokens.forEach(token => {
      tokensMap.set(token.address.toLowerCase(), {
        ...token,
        balance: token.balance || '0', // Ensure balance is at least '0'
        isCustom: true
      } as any);
    });
    
    // Add/update tokens with balance
    const safeAllTokensWithBalance = Array.isArray(allTokensWithBalance) ? allTokensWithBalance : [];
    safeAllTokensWithBalance.forEach(token => {
      const key = token.address.toLowerCase();
      const existing = tokensMap.get(key);
      
      if (existing) {
        // Update balance for existing custom token
        tokensMap.set(key, {
          ...existing,
          balance: token.balance || existing.balance || '0'
        });
      } else {
        // Add non-custom token with balance
        tokensMap.set(key, token);
      }
    });
    
    return Array.from(tokensMap.values());
  }, [chainId, address, getCustomTokensForWallet, allTokensWithBalance, tokenListVersion]);
  
  // Get loading state
  const loadingState = useMemo(() => {
    if (!chainId) return null;
    return loadingStates[chainId];
  }, [chainId, loadingStates]);
  

  
  // Get transactions directly from store state
  const allTransactions = useChainStore(state => state.transactions);
  const transactions = useMemo(() => {
    if (!address || !chainId) return [];
    const walletTxs = allTransactions[address.toLowerCase()] || [];
    // Filter by chainId và sắp xếp theo timestamp
    const chainTxs = walletTxs
      .filter(tx => tx.chainId === chainId)
      .sort((a, b) => b.timestamp - a.timestamp);
    
    return chainTxs.slice(0, 20);
  }, [address, chainId, allTransactions]);
  
  // Get provider function for gas estimation and custom operations
  const getProvider = useCallback(async () => {
    if (!chainId) return null;
    
    try {
      // Use custom public client from selected RPC
      if (customPublicClient) {
        return customPublicClient;
      }
      
      // Fallback to default wagmi client
      return publicClient || null;
    } catch (error) {
      console.error('Failed to get provider:', error);
      return publicClient || null;
    }
  }, [chainId, customPublicClient, publicClient]);
  
  // Save last selected chain when switching
  useEffect(() => {
    if (address && chainId) {
      setLastSelectedChain(address, chainId);
    }
  }, [address, chainId, setLastSelectedChain]);
  
  // Load native token price
  useEffect(() => {
    if (chainInfo?.gecko_id) {
      priceService.getNativeTokenPrice(chainInfo.gecko_id).then(price => {
        setNativeTokenPrice(price);
      });
    }
  }, [chainInfo]);
  
  // Load token prices
  useEffect(() => {
    if (tokens.length === 0) return;
    
    const tokenAddresses = tokens.map(t => t.address);
    
    priceService.getTokenPrices(chainId, tokenAddresses).then(prices => {
      setTokenPrices(prices);
    });
  }, [tokens, chainId]);
  
  // Load RPCs when chain changes
  useEffect(() => {
    if (!chainId) return;
    
    rpcManager.getRpcsForChain(chainId).then(rpcList => {
      setRpcs(rpcList);
    });
  }, [chainId]);
  
  // Load custom tokens balance when chain or wallet changes
  useEffect(() => {
    if (!chainId || !address) return;
    
    // Call updateCustomTokenBalances directly
    const loadTokenBalances = async () => {
      const customTokens = getCustomTokensForWallet(address, chainId);
      const safeCustomTokens = Array.isArray(customTokens) ? customTokens : [];
      if (safeCustomTokens.length > 0) {
        setIsLoadingBalance(true);
        setBalancesLoading(chainId, true);
        
        try {
          // Get custom provider with selected RPC
          const provider = await getProvider();
          if (!provider) return;
          
          // Get RPCs for fallback
          const rpcs = await rpcManager.getRpcsForChain(chainId);
          
          const balanceUpdates = await tokenManager.updateTokenBalances(
            provider as any, // Cast to any to handle both types
            safeCustomTokens as any,
            address,
            rpcs
          );
          
          const updatedTokens = safeCustomTokens.map(token => {
            const update = balanceUpdates.find(u => u.address.toLowerCase() === token.address.toLowerCase());
            if (update && update.success) {
              return { ...token, balance: update.balance };
            }
            return token;
          });
          
          setAllTokensWithBalance(prev => {
            const nonCustom = prev.filter(t => !t.isCustom);
            return [...nonCustom, ...updatedTokens] as any;
          });
        } catch (error) {
          console.error('Failed to load token balances:', error);
        } finally {
          setIsLoadingBalance(false);
          setBalancesLoading(chainId, false);
        }
      }
    };
    
    loadTokenBalances();
  }, [chainId, address, getCustomTokensForWallet, setBalancesLoading, getProvider]);
  
  // Check for imported wallet
  useEffect(() => {
    try {
      const storedWallet = localStorage.getItem('helio-imported-wallet');
      if (storedWallet) {
        const parsed = JSON.parse(storedWallet);
        setImportedWallet(parsed);
      }
    } catch (error) {
      console.error('Failed to load imported wallet:', error);
    }
  }, []);
  
  // Track transaction status when hash is available
  useEffect(() => {
    if (sendTxHash && address && chainId && pendingTxInfo) {
      // Add to transaction history with the real hash
      const walletTxs = allTransactions[address.toLowerCase()] || [];
      const existingTxs = walletTxs.filter(tx => tx.chainId === chainId);
      const hasTx = existingTxs.some(tx => tx.hash === sendTxHash);
      
      if (!hasTx) {
        addTransaction(address, chainId, {
          hash: sendTxHash,
          from: address,
          to: pendingTxInfo.to,
          value: pendingTxInfo.value,
          type: 'send',
          status: 'pending',
          timestamp: Date.now(),
          chainId
        });
        
        // Clear pending info after adding
        setPendingTxInfo(null);
      }
    }
  }, [sendTxHash, address, chainId, addTransaction, allTransactions, pendingTxInfo]);
  
  useEffect(() => {
    if (writeTxHash && address && chainId && pendingTxInfo) {
      // Add to transaction history with the real hash
      const walletTxs = allTransactions[address.toLowerCase()] || [];
      const existingTxs = walletTxs.filter(tx => tx.chainId === chainId);
      const hasTx = existingTxs.some(tx => tx.hash === writeTxHash);
      
      if (!hasTx) {
        addTransaction(address, chainId, {
          hash: writeTxHash,
          from: address,
          to: pendingTxInfo.to,
          value: pendingTxInfo.value,
          type: 'token',
          status: 'pending',
          timestamp: Date.now(),
          chainId,
          tokenAddress: pendingTxInfo.tokenAddress,
          tokenSymbol: pendingTxInfo.tokenSymbol
        });
        
        // Clear pending info after adding
        setPendingTxInfo(null);
      }
    }
  }, [writeTxHash, address, chainId, addTransaction, allTransactions, pendingTxInfo]);
  
  // Update custom token balances
  const updateCustomTokenBalances = useCallback(async () => {
    if (!chainId || !address) return;
    
    const customTokens = getCustomTokensForWallet(address, chainId);
    const safeCustomTokens = Array.isArray(customTokens) ? customTokens : [];
    if (safeCustomTokens.length === 0) return;
    
    setIsLoadingBalance(true);
    setBalancesLoading(chainId, true);
    
    try {
      // Get RPCs for fallback first
      const rpcs = await rpcManager.getRpcsForChain(chainId);
      
      // Get provider with fallback
      const provider = await getProvider();
      if (!provider) {
        // Fallback to publicClient if getProvider fails
        const clientToUse = customPublicClient || publicClient;
        if (!clientToUse) {
          throw new Error('No provider available');
        }
        // Use clientToUse as provider
        const balanceUpdates = await tokenManager.updateTokenBalances(
          clientToUse as any,
          safeCustomTokens as any,
          address,
          rpcs
        );
        
        // Update tokens with new balances
        const updatedTokens = safeCustomTokens.map(token => {
          const update = balanceUpdates.find(u => u.address.toLowerCase() === token.address.toLowerCase());
          if (update && update.success) {
            return { ...token, balance: update.balance };
          }
          return token;
        });
        
        // Update state
        setAllTokensWithBalance(prev => {
          const nonCustom = prev.filter(t => !t.isCustom);
          return [...nonCustom, ...updatedTokens] as any;
        });
        
        return;
      }
      
      // Update balances using custom provider
      // If provider is publicClient (viem), use it directly
      // Otherwise it's ethers provider from rpcManager
      const balanceUpdates = await tokenManager.updateTokenBalances(
        provider as any, // Cast to any to handle both types
        safeCustomTokens as any,
        address,
        rpcs
      );
      
      // Update tokens with new balances
      const updatedTokens = safeCustomTokens.map(token => {
        const update = balanceUpdates.find(u => u.address.toLowerCase() === token.address.toLowerCase());
        if (update && update.success) {
          return { ...token, balance: update.balance };
        }
        return token;
      });
      
      // Update state
      setAllTokensWithBalance(prev => {
        const nonCustom = prev.filter(t => !t.isCustom);
        return [...nonCustom, ...updatedTokens] as any;
      });
      
    } catch (error) {
      console.error('Failed to update custom token balances:', error);
    } finally {
      setIsLoadingBalance(false);
      setBalancesLoading(chainId, false);
    }
  }, [chainId, address, getCustomTokensForWallet, setBalancesLoading, getProvider]);
  
  // Force refetch balance when chain changes
  useEffect(() => {
    if (address && chainId) {
      refetchBalance();
      // Also update custom token balances
      updateCustomTokenBalances();
    }
  }, [chainId, address, refetchBalance, updateCustomTokenBalances]);
  
  // Auto-refresh balances periodically
  useEffect(() => {
    if (!isConnected || !chainId || !address) return;
    
    const interval = setInterval(() => {
      // Refresh native balance
      customRefetchBalance();
      // Refresh custom token balances
      updateCustomTokenBalances();
    }, 60000); // Every minute
    
    return () => clearInterval(interval);
  }, [isConnected, chainId, address, customRefetchBalance, updateCustomTokenBalances]);

  // Reload balance function
  const reloadBalance = useCallback(async () => {
    if (!isConnected || !address) return;
    
    // Refresh native balance using custom RPC
    await customRefetchBalance();
    
    // Refresh custom token balances
    await updateCustomTokenBalances();
  }, [isConnected, address, customRefetchBalance, updateCustomTokenBalances]);
  
  // Add custom token
  const addCustomToken = async (tokenAddress: string): Promise<Token | null> => {
    if (!chainId || !address) {
      throw new Error('Wallet not connected');
    }
    
    // Use customPublicClient if available, otherwise fallback to default publicClient
    const clientToUse = customPublicClient || publicClient;
    if (!clientToUse) {
      throw new Error('No public client available');
    }
    
    // Validate address format
    if (!isAddress(tokenAddress)) {
      throw new Error('Invalid token address');
    }
    
    // Check if already exists as custom token
    const existingCustom = getCustomTokensForWallet(address, chainId);
    const safeExistingCustom = Array.isArray(existingCustom) ? existingCustom : [];
    if (safeExistingCustom.some(t => t.address.toLowerCase() === tokenAddress.toLowerCase())) {
      throw new Error('Token already added');
    }
    
    try {
      // Use viem directly to read token info (like in old code)
      const tokenAbi = [
        {
          name: 'name',
          type: 'function',
          stateMutability: 'view',
          inputs: [],
          outputs: [{ name: '', type: 'string' }]
        },
        {
          name: 'symbol',
          type: 'function',
          stateMutability: 'view',
          inputs: [],
          outputs: [{ name: '', type: 'string' }]
        },
        {
          name: 'decimals',
          type: 'function',
          stateMutability: 'view',
          inputs: [],
          outputs: [{ name: '', type: 'uint8' }]
        },
        {
          name: 'balanceOf',
          type: 'function',
          stateMutability: 'view',
          inputs: [{ name: 'account', type: 'address' }],
          outputs: [{ name: '', type: 'uint256' }]
        }
      ] as const;
      
      let name = 'Unknown Token';
      let symbol = 'UNKNOWN';
      let decimals = 18;
      let balance = '0';
      
      // Try to get token info using viem
      try {
        const [nameResult, symbolResult, decimalsResult] = await Promise.all([
          clientToUse.readContract({
            address: tokenAddress as Address,
            abi: tokenAbi,
            functionName: 'name'
          }).catch(() => null),
          clientToUse.readContract({
            address: tokenAddress as Address,
            abi: tokenAbi,
            functionName: 'symbol'
          }).catch(() => null),
          clientToUse.readContract({
            address: tokenAddress as Address,
            abi: tokenAbi,
            functionName: 'decimals'
          }).catch(() => null)
        ]);
        
        if (nameResult) name = nameResult;
        if (symbolResult) symbol = symbolResult;
        if (decimalsResult !== null) decimals = Number(decimalsResult);
        
        // Get balance
        const balanceResult = await clientToUse.readContract({
          address: tokenAddress as Address,
          abi: tokenAbi,
          functionName: 'balanceOf',
          args: [address as Address]
        });
        
        if (balanceResult !== undefined) {
          balance = formatUnits(balanceResult, decimals);
        }
      } catch (error) {
        console.error('Error reading token info:', error);
        // If all failed, it's not a valid token
        if (name === 'Unknown Token' && symbol === 'UNKNOWN') {
          throw new Error('Not a valid ERC20 token');
        }
      }
      
      const token: Token = {
        address: tokenAddress.toLowerCase(),
        name,
        symbol,
        decimals,
        balance,
        isCustom: true,
        source: 'custom' as const,
        chainId,
        addedAt: Date.now()
      };
      
      // Store in zustand store
      addCustomTokenToWallet(address, chainId, token as any);
      
      // Update state to show new token - ensure it propagates immediately
      setAllTokensWithBalance(prev => {
        const newTokens = [...prev, token as any];
        return newTokens;
      });
      
      // Force re-render of token list
      setTokenListVersion(v => v + 1);
      
      // Emit event for other components to update
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('token-added', {
          detail: { token, chainId, address }
        }));
      }, 200);
      
      // Update token balance immediately
      setTimeout(() => {
        updateCustomTokenBalances();
      }, 300);
      
      // Fetch price
      const prices = await priceService.getTokenPrices(chainId, [token.address]);
      if (prices[token.address.toLowerCase()]) {
        setTokenPrices(prev => ({
          ...prev,
          [token.address.toLowerCase()]: prices[token.address.toLowerCase()]
        }));
      }
      
      return token;
    } catch (error: any) {
      console.error('Add token error:', error);
      throw new Error(error.message || 'Failed to add token');
    }
  };
  
  // Scan for popular tokens with balance
  const scanPopularTokens = async (): Promise<Token[]> => {
    if (!chainId || !address) {
      throw new Error('Wallet not connected');
    }
    
    // Use customPublicClient if available, otherwise fallback to default publicClient
    const clientToUse = customPublicClient || publicClient;
    if (!clientToUse) {
      throw new Error('No public client available');
    }
    
    setIsLoadingTokens(true);
    setTokensLoading(chainId, true);
    
    try {
      // Get popular tokens for this chain
      const popularTokens = await tokenManager.fetchPopularTokens(chainId, 20);
      
      if (popularTokens.length === 0) {
        return [];
      }
      
      // Convert to Token format for balance check
      const tokensToCheck = popularTokens.map(info => ({
        ...info,
        chainId,
        balance: '0',
        source: 'popular' as const,
        addedAt: Date.now()
      }));
      
      // Get RPCs for fallback
      const rpcs = await rpcManager.getRpcsForChain(chainId);
      
      // Check balances using public client
      const balanceUpdates = await tokenManager.updateTokenBalances(
        clientToUse as any,
        tokensToCheck as any,
        address,
        rpcs
      );
      
      // Filter tokens with balance > 0
      const tokensWithBalance: Token[] = [];
      
      tokensToCheck.forEach((token, index) => {
        const balanceUpdate = balanceUpdates.find(u => u.address.toLowerCase() === token.address.toLowerCase());
        if (balanceUpdate && balanceUpdate.success) {
          const balance = parseFloat(balanceUpdate.balance);
          if (balance > 0) {
            tokensWithBalance.push({
              ...token,
              balance: balanceUpdate.balance
            });
          }
        }
      });
      
      if (tokensWithBalance.length > 0) {
        // Update state to show tokens with balance
        setAllTokensWithBalance(prev => {
          const existing = new Set(prev.map(t => t.address.toLowerCase()));
          const newTokens = tokensWithBalance.filter(t => !existing.has(t.address.toLowerCase()));
          return [...prev, ...newTokens] as any;
        });
        
        // Fetch prices
        const addresses = tokensWithBalance.map(t => t.address);
        const prices = await priceService.getTokenPrices(chainId, addresses);
        setTokenPrices(prev => ({ ...prev, ...prices }));
      }
      
      return tokensWithBalance;
      
    } catch (error: any) {
      console.error('Scan popular tokens error:', error);
      throw new Error(error.message || 'Failed to scan tokens');
    } finally {
      setIsLoadingTokens(false);
      setTokensLoading(chainId, false);
    }
  };
  
  // Remove custom token
  const removeCustomToken = (tokenAddress: string) => {
    if (!chainId || !address) return;
    
    // Remove from store
    removeCustomTokenFromWallet(address, chainId, tokenAddress);
    
    // Remove from local state
    setAllTokensWithBalance(prev => 
      prev.filter(t => t.address.toLowerCase() !== tokenAddress.toLowerCase())
    );
  };
  
  // Get exact native balance from blockchain
  const getExactNativeBalance = async (): Promise<string> => {
    if (!address) {
      throw new Error('Wallet not connected');
    }
    
    // Use customPublicClient if available, otherwise fallback to default publicClient
    const clientToUse = customPublicClient || publicClient;
    if (!clientToUse) {
      throw new Error('No public client available');
    }
    
    try {
      const balance = await clientToUse.getBalance({ address: address as Address });
      const formatted = formatUnits(balance, 18);
      return formatted;
    } catch (error) {
      console.error('Failed to get exact balance:', error);
      throw error;
    }
  };
  
  // Get exact token balance from blockchain
  const getExactTokenBalance = async (tokenAddress: string, decimals: number): Promise<string> => {
    if (!address) {
      throw new Error('Wallet not connected');
    }
    
    // Use customPublicClient if available, otherwise fallback to default publicClient
    const clientToUse = customPublicClient || publicClient;
    if (!clientToUse) {
      throw new Error('No public client available');
    }
    
    try {
      const result = await clientToUse.readContract({
        address: tokenAddress as Address,
        abi: ERC20_ABI,
        functionName: 'balanceOf',
        args: [address as Address]
      });
      
      const formatted = formatUnits(result, decimals);
      return formatted;
    } catch (error) {
      console.error('Failed to get exact token balance:', error);
      throw error;
    }
  };
  
  // Send native token
  const onSend = useCallback(async (to: string, value: string) => {
    if (!address || !chainId) return;
    
    try {
      // Save transaction info for later
      setPendingTxInfo({
        to,
        value
      });
      
      // Use Wagmi sendTransaction hook (will use patched RPC via useRpcPatcher)
      await sendTransaction({
        to: to as Address,
        value: parseEther(value)
      });
      
      // Transaction submitted successfully
      toast.success('Transaction sent!');
      
      // Refresh balance after a delay
      setTimeout(() => {
        customRefetchBalance();
      }, 5000);
      
    } catch (error: any) {
      console.error('Send error:', error);
      toast.error(error.message || 'Transaction failed');
    }
  }, [address, chainId, sendTransaction, customRefetchBalance]);
  
  // Send token
  const onSendToken = useCallback(async (tokenAddress: string, to: string, amount: string, decimals: number) => {
    if (!address || !chainId) return;
    
    try {
      // Get token info for the transaction
      const safeAllTokensWithBalance = Array.isArray(allTokensWithBalance) ? allTokensWithBalance : [];
      const token = safeAllTokensWithBalance.find(t => 
        t.address.toLowerCase() === tokenAddress.toLowerCase()
      );
      
      // Save transaction info for later
      setPendingTxInfo({
        to,
        value: amount,
        tokenAddress,
        tokenSymbol: token?.symbol || 'TOKEN'
      });
      
      // Use Wagmi writeContract hook (will use patched RPC via useRpcPatcher)
      await writeContract({
        address: tokenAddress as Address,
        abi: ERC20_ABI,
        functionName: 'transfer',
        args: [to as Address, parseUnits(amount, decimals)]
      });
      
      // Transaction submitted successfully
      toast.success('Token transfer sent!');
      
      // Refresh balances after a delay
      setTimeout(() => {
        updateCustomTokenBalances();
      }, 5000);
      
    } catch (error: any) {
      console.error('Send token error:', error);
      toast.error(error.message || 'Token transfer failed');
    }
  }, [address, chainId, writeContract, allTokensWithBalance, updateCustomTokenBalances]);
  
  // Switch chain
  const switchToChain = async (targetChainId: number) => {
    try {
      await switchChain({ chainId: targetChainId });
      return true;
    } catch (error: any) {
      console.error('Chain switch failed:', error);
      toast.error('Failed to switch chain');
      return false;
    }
  };
  
  // Change RPC
  const changeRpc = async (newRpc: string) => {
    if (!chainId) return;
    
    // Optimistic update - update UI immediately
    setSelectedRpc(newRpc);
    incrementRpcSwitchCount(chainId);
    
    // Update chain preferences
    setChainPreferences(chainId, {
      lastUsedRpc: newRpc,
      lastRpcSwitchTime: Date.now(),
      preferredRpc: newRpc
    });
    
    // Test new RPC in background
    rpcManager.testRpc(newRpc, chainId).then(health => {
      updateRpcHealth(newRpc, health);
    }).catch(error => {
      console.error('Failed to test new RPC:', error);
    });
    
    // Clear provider cache to use new RPC
    rpcManager.clearProvider(chainId);
    
    // Force refresh balances with new RPC
    setTimeout(() => {
      customRefetchBalance();
      updateCustomTokenBalances();
    }, 1000);
  };
  
  // Test best RPC
  /*
  const testBestRpc = async () => {
    if (!chainId || !rpcs || rpcs.length === 0) return;
    
    setIsTestingRpc(true);
    try {
      const results = await rpcManager.testBatchRpcs(rpcs, chainId);
      const best = results
        .filter(r => r.latency < Infinity)
        .sort((a, b) => a.latency - b.latency)[0];
      
      if (best) {
        setSelectedRpc(best.url);
        toast.success(`Best RPC: ${new URL(best.url).hostname} (${best.latency}ms)`);
      }
    } finally {
      setIsTestingRpc(false);
    }
  };
  */
  
  // Auto switch RPC
  /*
  const autoSwitchRpc = async () => {
    if (!chainId) return;
    
    const preferences = getChainPreferences(chainId);
    if (!preferences.autoSwitchRpc) return;
    
    // Test current RPC
    if (selectedRpc) {
      const health = await rpcManager.testRpc(selectedRpc, chainId);
      if (health.latency < 1000) return; // Current RPC is good
    }
    
    // Test all and switch to best
    await testBestRpc();
  };
  */
  
  // Get RPC health status
  const getCurrentRpcHealth = () => {
    if (!selectedRpc) return null;
    return getRpcHealth(selectedRpc);
  };
  
  // Get chain preferences
  /*
  const getCurrentChainPreferences = () => {
    if (!chainId) return null;
    return getChainPreferences(chainId);
  };
  */
  
  // Toggle auto-switch RPC
  /*
  const toggleAutoSwitchRpc = () => {
    if (!chainId) return;
    const preferences = getChainPreferences(chainId);
    const newEnabled = !preferences.autoSwitchRpc;
    setAutoSwitchRpc(chainId, newEnabled);
    toast.success(`Auto-switch RPC ${newEnabled ? 'enabled' : 'disabled'}`);
  };
  */
  
  // Batch test RPCs
  /*
  const batchTestRpcs = async () => {
    if (!chainId || !rpcs || rpcs.length === 0) return [];
    
    try {
      const results = await rpcManager.batchTestRpcs(chainId, rpcs);
      return results;
    } catch (error) {
      console.error('Failed to batch test RPCs:', error);
      return [];
    }
  };
  */
  
  // Import wallet functions
  const importWallet = async (data: { type: 'mnemonic' | 'privateKey'; value: string }) => {
    // This would be handled by the connector
    // Store encrypted data in localStorage
    try {
      const walletData = {
        type: data.type,
        [data.type === 'mnemonic' ? 'mnemonic' : 'privateKey']: data.value
      };
      
      // In production, encrypt this
      localStorage.setItem('helio-imported-wallet', JSON.stringify({
        type: data.type,
        encryptedData: btoa(JSON.stringify(walletData)),
        timestamp: Date.now(),
        isAutoConnect: true
      }));
      
      toast.success('Wallet imported successfully');
      return true;
    } catch (error) {
      console.error('Failed to import wallet:', error);
      toast.error('Failed to import wallet');
      return false;
    }
  };
  
  const clearImportedWallet = () => {
    localStorage.removeItem('helio-imported-wallet');
    setImportedWallet(null);
    toast.success('Imported wallet removed');
  };

  // Check for auto-connect on mount
  useEffect(() => {
    const checkAutoConnect = async () => {
      try {
        const storedWallet = localStorage.getItem('helio-imported-wallet');
        if (storedWallet) {
          const parsed = JSON.parse(storedWallet);
          setImportedWallet(parsed);
          
          // Check if auto-connect is enabled and wallet was connected recently
          if (parsed.isAutoConnect && parsed.lastConnectedAt) {
            const isRecent = Date.now() - parsed.lastConnectedAt < 24 * 60 * 60 * 1000;
            if (isRecent) {
              // Auto-connect will be handled by wagmi's auto-connect feature
            }
          }
        }
      } catch (error) {
        console.error('Failed to check auto-connect:', error);
      }
    };
    
    checkAutoConnect();
  }, []);
  
  // Update custom token balances when token added
  useEffect(() => {
    const handleTokenAdded = (e: any) => {
      if (e.detail.chainId === chainId && e.detail.address === address) {
        // Force update custom token balances
        updateCustomTokenBalances();
      }
    };
    
    window.addEventListener('token-added', handleTokenAdded);
    return () => window.removeEventListener('token-added', handleTokenAdded);
  }, [chainId, address, updateCustomTokenBalances]);
  
  // Initialize and update balance
  useEffect(() => {
    if (!address || !chainId) return;
    updateCustomTokenBalances();
  }, [address, chainId, updateCustomTokenBalances]);
  
  // Update transaction status when confirmed
  useEffect(() => {
    if (isConfirmedNative && sendTxHash && address && chainId) {
      updateTransactionStatus(address, chainId, sendTxHash, 'confirmed');
      // Refresh balance after confirmation
      customRefetchBalance();
    }
  }, [isConfirmedNative, sendTxHash, address, chainId, updateTransactionStatus, customRefetchBalance]);
  
  useEffect(() => {
    if (isConfirmedToken && writeTxHash && address && chainId) {
      updateTransactionStatus(address, chainId, writeTxHash, 'confirmed');
      // Refresh token balances after confirmation
      updateCustomTokenBalances();
    }
  }, [isConfirmedToken, writeTxHash, address, chainId, updateTransactionStatus, updateCustomTokenBalances]);
  
  // Remove transaction
  const onRemoveTransaction = useCallback((txHash: string) => {
    if (!address || !chainId) return;
    removeTransaction(address, chainId, txHash);
  }, [address, chainId, removeTransaction]);

  // Clear all transactions for current chain
  const onClearTransactions = useCallback(() => {
    if (address && chainId) {
      clearTransactions(address, chainId);
    }
  }, [address, chainId, clearTransactions]);

  const onAddTransactions = useCallback((newTransactions: any[]) => {
    if (address && chainId) {
      // Add each transaction to the store
      newTransactions.forEach(tx => {
        addTransaction(address, chainId, tx);
      });
    }
  }, [address, chainId, addTransaction]);

  return {
    // Account
    address,
    isConnected,
    
    // Chain
    chainId,
    chainInfo,
    chains,
    switchChain: switchToChain,
    
    // Balance
    balance,
    nativeTokenPrice,
    reloadBalance,
    isLoadingBalance: isLoadingBalance || loadingState?.balancesLoading || false,
    getExactNativeBalance,
    getExactTokenBalance,
    
    // Tokens - simplified for new design
    tokens,
    tokenPrices,
    isLoadingTokens: isLoadingTokens || loadingState?.tokensLoading || false,
    addCustomToken,
    scanPopularTokens,
    removeCustomToken,
    
    // Transactions
    transactions,
    onSend,
    onSendToken,
    isSending: isSendingNative || isSendingToken,
    onUpdateTransactionStatus: (txHash: string, status: 'confirmed' | 'failed') => {
      if (address && chainId) {
        updateTransactionStatus(address, chainId, txHash, status);
      }
    },
    onRemoveTransaction,
    onClearTransactions,
    onAddTransactions,
    
    // RPC
    rpcs,
    selectedRpc,
    changeRpc: setSelectedRpc,
    // testBestRpc,
    // batchTestRpcs,
    isTestingRpc,
    // autoSwitchRpc,
    getCurrentRpcHealth,
    preferences: getChainPreferences(chainId),
    // toggleAutoSwitchRpc,
    
    // Provider
    getProvider,
    publicClient,
    walletClient,
    
    // Import wallet
    importedWallet,
    importWallet,
    clearImportedWallet
  };
}